import { useQuery } from '@tanstack/react-query';
import { ProductsApi, Permission } from '@up2stay/up2promo-api-client';
import { useMemo } from 'react';

import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useLocations } from '@/core/hooks/use-locations';
import { usePermissions } from '@/core/hooks/use-permissions';
import { usePhysicalBusinessCards } from '@/core/hooks/use-physical-business-cards';
import { useProfile } from '@/core/hooks/use-profile';
import { useChannelLocations } from '@/core/hooks/use-reputation';
import { useWorkspaceHasTeam } from '@/core/hooks/use-workspace-has-team';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export type GetStartedDomainTruth = {
  hasLocation: boolean;
  hasProduct: boolean;
  hasGoogleLinked: boolean;
  hasTeam: boolean;
  hasProfile: boolean;
  hasCard: boolean;
  canManageLocations: boolean;
  canManageProducts: boolean;
  canInviteMembers: boolean;
  canSetupReputation: boolean;
  isDomainReady: boolean;
};

export function useGetStartedDomain(): GetStartedDomainTruth {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { can, isLoading: permissionsLoading } = usePermissions();
  const { isFeatureEnabled, isLoading: entitlementsLoading } =
    useEntitlements();

  const canManageLocations = can(Permission.LOCATIONS_MANAGE);
  const canManageProducts = can(Permission.PRODUCTS_MANAGE);
  const canInviteMembers =
    isFeatureEnabled('feature.invite_members') &&
    can(Permission.MEMBERS_INVITE);
  const canSetupReputation =
    isFeatureEnabled('feature.reputation') &&
    can(Permission.REPUTATION_CONFIGURE);

  const { locations, isLoading: locationsLoading } = useLocations(workspaceId, {
    limit: 1,
    enabled: Boolean(workspaceId),
  });

  const productsQuery = useQuery({
    queryKey: ['guidance', 'product-count', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return 0;
      const res = await ProductsApi.findAll({
        path: { workspaceId },
        query: { page: 1, limit: 1 },
      });
      return res.data?.totalCount ?? 0;
    },
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
  });

  const channelsQuery = useChannelLocations(
    canSetupReputation ? workspaceId : undefined,
  );

  const { activeMemberCount, isLoading: teamLoading } =
    useWorkspaceHasTeam(workspaceId);

  const { profile, isLoading: profileLoading } = useProfile();
  const { cards, isLoading: cardsLoading } = usePhysicalBusinessCards();

  return useMemo(() => {
    const hasGoogleLinked = (channelsQuery.data ?? []).some(
      (channel) => channel.provider === 'google' && channel.linked,
    );

    const isDomainReady =
      Boolean(workspaceId) &&
      !permissionsLoading &&
      !entitlementsLoading &&
      !locationsLoading &&
      !productsQuery.isLoading &&
      !teamLoading &&
      !profileLoading &&
      !cardsLoading &&
      (!canSetupReputation || !channelsQuery.isLoading);

    return {
      hasLocation: locations.length >= 1,
      hasProduct: (productsQuery.data ?? 0) >= 1,
      hasGoogleLinked,
      hasTeam: activeMemberCount >= 2,
      hasProfile: profile != null,
      hasCard: cards.length >= 1,
      canManageLocations,
      canManageProducts,
      canInviteMembers,
      canSetupReputation,
      isDomainReady,
    };
  }, [
    workspaceId,
    permissionsLoading,
    entitlementsLoading,
    locationsLoading,
    productsQuery.isLoading,
    productsQuery.data,
    teamLoading,
    profileLoading,
    cardsLoading,
    canSetupReputation,
    channelsQuery.isLoading,
    channelsQuery.data,
    locations.length,
    activeMemberCount,
    profile,
    cards.length,
    canManageLocations,
    canManageProducts,
    canInviteMembers,
  ]);
}
